import { useAuthStore } from 'lib/stores/authStore';
import { useUserPositionsStore } from 'lib/stores/userPositionsStore';
import * as datadogRum from 'lib/telemetry/datadogRum';
import { useMarketEntitiesStore } from 'stores/zustand/marketEntitiesStore';
import { memoryMonitor } from 'transport/memoryMonitor';

const POLL_INTERVAL_MS = 30_000;

class DatadogContextManager {
    private intervalId: ReturnType<typeof setInterval> | null = null;

    start(): void {
        if (this.intervalId !== null) return;

        datadogRum.setGlobalContextProperty(
            'device_memory_gb',
            (navigator as Navigator & { deviceMemory?: number }).deviceMemory ?? null,
        );

        this.tick();
        this.intervalId = setInterval(this.tick, POLL_INTERVAL_MS);
    }

    stop(): void {
        if (this.intervalId !== null) {
            clearInterval(this.intervalId);
            this.intervalId = null;
        }
    }

    /** Single tick: refresh context properties, then emit memory sample with up-to-date tags. */
    private tick = (): void => {
        if (document.visibilityState === 'hidden') return;

        this.poll();
        memoryMonitor.sample();
    };

    private poll(): void {
        try {
            datadogRum.setGlobalContextProperty('is_logged_in', useAuthStore.getState().isLoggedIn);
            const livePositions = useUserPositionsStore.getState().positions;
            const eventPositionCount = Object.keys(livePositions).length;
            const positionCount = Object.values(livePositions).reduce(
                (sum, entries) => sum + Object.keys(entries).length,
                0,
            );
            datadogRum.setGlobalContextProperty('position_count', positionCount);
            datadogRum.setGlobalContextProperty('event_position_count', eventPositionCount);
            const { events, markets } = useMarketEntitiesStore.getState();
            datadogRum.setGlobalContextProperty('loaded_event_count', Object.keys(events).length);
            datadogRum.setGlobalContextProperty('loaded_market_count', Object.keys(markets).length);
        } catch {
            // Datadog or stores may not be ready
        }
    }
}

export const datadogContextManager = new DatadogContextManager();
